/**
 * Where a coachmark's cut-out and its card go, given an element and a viewport.
 *
 * SEPARATE FROM THE COMPONENT because this is the part that is actually wrong when a
 * tour looks broken. "The card hangs off the bottom of the screen" and "the highlight
 * is a few pixels out" are arithmetic bugs, and arithmetic can be tested against a
 * laptop, a phone and an element half off the edge without rendering anything.
 *
 * Everything here works in VIEWPORT coordinates, which is what getBoundingClientRect
 * returns and what `position: fixed` consumes. No scroll offsets anywhere: adding them
 * is the classic way these drift by exactly the scroll distance.
 */

/** A rectangle in viewport coordinates. The shape getBoundingClientRect returns. */
export interface Box {
  top: number;
  left: number;
  width: number;
  height: number;
}

export interface Viewport {
  width: number;
  height: number;
}

/** Which side of the anchor the card is asked to sit on. */
export type Side = 'top' | 'bottom' | 'left' | 'right';

/** How far the cut-out is grown beyond the element, so the highlight has a margin. */
export const PAD = 6;

/** The gap between the cut-out and the card. */
export const GAP = 12;

/** How close to the viewport edge the card may come. */
export const MARGIN = 12;

/**
 * The highlighted rectangle: the element, grown by PAD, clamped to the viewport.
 *
 * Clamped because an element can be partly off-screen - a lane below the fold, a tab
 * on a narrow window - and a cut-out with a negative left spills the ring across the
 * edge, which draws as a bright band down the side of the page.
 */
export function cutout(box: Box, viewport: Viewport): Box {
  const top = Math.max(0, box.top - PAD);
  const left = Math.max(0, box.left - PAD);
  const right = Math.min(viewport.width, box.left + box.width + PAD);
  const bottom = Math.min(viewport.height, box.top + box.height + PAD);
  return {
    top,
    left,
    width: Math.max(0, right - left),
    height: Math.max(0, bottom - top),
  };
}

/**
 * Whether the card fits on `side` of the anchor without leaving the viewport.
 *
 * Measured against the card's own size rather than a guess, because the cards are
 * different heights - one sentence or four - and a fixed estimate puts the short ones
 * too far away and pushes the long ones off the screen.
 */
export function fits(side: Side, hole: Box, card: Viewport, viewport: Viewport): boolean {
  if (side === 'top') {
    return hole.top - GAP - card.height >= MARGIN;
  }
  if (side === 'bottom') {
    return hole.top + hole.height + GAP + card.height <= viewport.height - MARGIN;
  }
  if (side === 'left') {
    return hole.left - GAP - card.width >= MARGIN;
  }
  return hole.left + hole.width + GAP + card.width <= viewport.width - MARGIN;
}

/**
 * The side to actually use.
 *
 * The preferred side if it fits, otherwise the first of the rest that does - in the
 * order below, chosen so a control in the masthead falls to `bottom` and one in a
 * toolbar falls to `top` rather than squeezing in beside it.
 *
 * If NOTHING fits, the preference is returned and `place` clamps it into the viewport.
 * That is the honest failure: the card overlaps the thing it describes on a very small
 * screen, rather than being positioned off the edge where it cannot be read at all.
 */
export function chooseSide(
  preferred: Side,
  hole: Box,
  card: Viewport,
  viewport: Viewport
): Side {
  const order: Side[] = [preferred, 'bottom', 'top', 'right', 'left'];
  for (const side of order) {
    if (fits(side, hole, card, viewport)) {
      return side;
    }
  }
  return preferred;
}

/** Keep `value` within [min, max], tolerating a range too small to satisfy. */
function clamp(value: number, min: number, max: number): number {
  if (max < min) {
    return min;
  }
  return Math.min(Math.max(value, min), max);
}

/**
 * Where to put the card, in viewport coordinates.
 *
 * Centred on the anchor along the free axis and clamped to the viewport, so a card
 * for a control at the far right edge slides left to stay readable instead of hanging
 * off it. The side has already been chosen; this only positions.
 */
export function place(
  side: Side,
  hole: Box,
  card: Viewport,
  viewport: Viewport
): { top: number; left: number } {
  const maxLeft = viewport.width - card.width - MARGIN;
  const maxTop = viewport.height - card.height - MARGIN;

  if (side === 'top' || side === 'bottom') {
    const top = side === 'top' ? hole.top - GAP - card.height : hole.top + hole.height + GAP;
    const centred = hole.left + hole.width / 2 - card.width / 2;
    return { top: clamp(top, MARGIN, maxTop), left: clamp(centred, MARGIN, maxLeft) };
  }

  const left = side === 'left' ? hole.left - GAP - card.width : hole.left + hole.width + GAP;
  const centred = hole.top + hole.height / 2 - card.height / 2;
  return { top: clamp(centred, MARGIN, maxTop), left: clamp(left, MARGIN, maxLeft) };
}

/**
 * Whether an element is worth pointing at.
 *
 * A zero-sized or display:none anchor is present in the DOM and invisible on screen -
 * a tab inside a collapsed nav, a button on a section that is folded away. Highlighting
 * one draws a ring around nothing in the top-left corner, which is the single worst
 * thing a tour can do, so the step is skipped instead.
 */
export function isVisible(box: Box | null): boolean {
  return Boolean(box && box.width > 0 && box.height > 0);
}
